import React, {useState} from "react"
import {isEqual} from "lodash"

import authentication from "@irati/auth"
import GenericModal from "@irati/ui/src/GenericModal"
import modalStory, {anyStoriesOpen} from "@irati/utils/modalStory"
import apps, {RECOGNITION} from "@irati-web/app/src/routeConfig"
import {injectIntl} from "@irati/utils/lang"
import {TEAMS_OPTION, EVERYONE_OPTION} from "@irati/recognition/constants/share"
import tracking from "@irati-tracking/recognition/recognition"

import {NewRecognition} from "@irati/recognition/apollo/mutation/Recognition/Create"
import {InjectIntlProps} from "@irati/utils/lang/injectIntl"
import FormatMessage from "@irati/utils/lang/types/FormatMessage"
import {Recognition} from "@irati/types"
import {CancelConfirm} from "@irati-web/recognition/src/components/Popup/shared"
import History, {appOrContext} from "@irati/utils/history"
import Create from "./Create"
import Success from "./Success"
import SuccessManager from "./SuccessManager"
import RewardRecognition from "@irati-web/recognition/src/components/Popup/RewardRecognition"

export const CREATE = "CREATE"
export const CANCEL_CONFIRM = "CANCEL_CONFIRM"
export const SUCCESS = "SUCCESS"
export const REWARD_RECOGNITION = "REWARD_RECOGNITION"
export const SUCCESS_MANAGER = "SUCCESS_MANAGER"

const modals = [CREATE, CANCEL_CONFIRM, SUCCESS, REWARD_RECOGNITION, SUCCESS_MANAGER]

export const EMPTY_RECOGNITION = (formatMessage: FormatMessage): NewRecognition => {
  const canShareWithEveryone = authentication.apps("recognition.recognition.everyone")
  return {
    companyValues: [],
    userIds: [],
    teamIds: [],
    message: "",
    isPrivate: true,
    share: canShareWithEveryone ? EVERYONE_OPTION(formatMessage) : TEAMS_OPTION(formatMessage)
  }
}
interface Props extends InjectIntlProps {}

const CreateRecognitionPopup: React.FC<Props> = (props) => {
  const {formatMessage} = props
  const [recognition, setRecognition] = useState<NewRecognition | Recognition>(EMPTY_RECOGNITION(formatMessage))
  const [show, setShow] = useState(modalStory(modals, CREATE))

  const canReward = authentication.apps("wallet.flexiRewards.canRewardEmployees")
  // looks for any active popups, if none; we know its a new instance of popup so clear recognition
  const shouldClearRecognition = () => !anyStoriesOpen(show)
  const createAgain = () => {
    if (show.SUCCESS_MANAGER) {
      tracking.createNewRecognition({type: "reward"})
    } else {
      tracking.createNewRecognition({type: canReward ? "manager_recognition" : "user_recognition"})
    }
    setRecognition(EMPTY_RECOGNITION(formatMessage))
    setShow(modalStory(modals, CREATE, show))
  }
  const showModal = (modalToShow = CREATE) => {
    const newRecognition = shouldClearRecognition() ? EMPTY_RECOGNITION(formatMessage) : recognition
    setRecognition(newRecognition)
    setShow(modalStory(modals, modalToShow, show))
  }
  const hide = () => {
    sendCancelEvent()
    setShow(modalStory(modals, null))
    History.push(appOrContext({to: "/polls", app: apps[RECOGNITION]}, {}))
  }
  const sendCancelEvent = () => {
    if (show.CREATE || show.CANCEL_CONFIRM) {
      tracking.recognitionCtaClick({
        source: "recognition_form",
        type: canReward ? "manager_recognition_cancel" : "user_recognition_cancel"
      })
    } else if (show.SUCCESS || show.REWARD_RECOGNITION || show.SUCCESS_MANAGER) {
      tracking.recognitionCtaClick({
        source: "confirmation_modal",
        type: show.SUCCESS_MANAGER ? "reward_cancel" : canReward ? "manager_recognition_cancel" : "user_recognition_cancel"
      })
    }
  }
  const cancelConfirm = () => {
    if (isEqual(recognition, EMPTY_RECOGNITION(formatMessage))) {
      return hide()
    }
    showModal(CANCEL_CONFIRM)
  }
  const onRecognitionChange = (newRecognition: NewRecognition | Recognition) => {
    setRecognition(newRecognition)
  }
  const onRecognitionCreated = (newRecognition: Recognition) => {
    showModal(canReward ? REWARD_RECOGNITION : SUCCESS)
    setRecognition(newRecognition)
  }
  const onRewardCreated = () => {
    tracking.rewardCreatedOnRecognition({
      type: "recognition"
    })
    showModal(SUCCESS_MANAGER)
  }
  const onErrorReward = () => {
    tracking.rewardErrorOnRecognition({
      source: "manager_recognition",
      type: "confirmation_modal"
    })
  }
  return (
    <>
      <GenericModal show={show[CREATE]} onHide={cancelConfirm} hideActions>
        <Create
          selectScreen={showModal}
          onRecognitionCreated={onRecognitionCreated}
          onRecognitionChange={onRecognitionChange}
          recognition={recognition as NewRecognition}
        />
      </GenericModal>
      <GenericModal show={show[REWARD_RECOGNITION]} customWidth="750" onHide={hide} hideActions>
        <RewardRecognition
          onErrorReward={onErrorReward}
          onClickButton={createAgain}
          onRewardCreated={onRewardCreated}
          recognition={recognition as Recognition}
          newRecognition
        />
      </GenericModal>
      <GenericModal show={show[SUCCESS]} onHide={hide} hideActions>
        <Success onClickButton={createAgain} onHide={hide} />
      </GenericModal>
      <GenericModal show={show[SUCCESS_MANAGER]} onHide={hide} hideActions>
        <SuccessManager onClickButton={createAgain} onHide={hide} newRecognition />
      </GenericModal>
      <GenericModal show={show[CANCEL_CONFIRM]} critical hideActions>
        <CancelConfirm previous={show.previous} selectScreen={showModal} onHide={hide} />
      </GenericModal>
    </>
  )
}

export default injectIntl(CreateRecognitionPopup)
